import React from "react";
import PropTypes from "prop-types";
import PreviewDialog from "../ui/dialogs/PreviewDialog";
import { CheckmarkFilled, ErrorCircle20Regular, Open16Filled } from "@fluentui/react-icons";
import { Badge, Divider, makeStyles, tokens } from "@fluentui/react-components";
import { VERSION_TYPES } from "../constants/versionTypes";
import { toCurrentOriginUrl } from "../utils/currentOriginUrl.js";

const previewBody = {
  gap: "8px",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  alignSelf: "stretch"
};

const useStyles = makeStyles({
  versionTypeContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    width: "100%"
  },
  urlLink: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline"
    }
  },
  urlText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "300px",
    fontSize: tokens.fontSizeBase200
  },
  versionTypeDescription: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200
  },
  versionTypeBadge: {
    width: "max-content",
    gap: tokens.spacingHorizontalSNudge,
    paddingBottom: "1.5px"
  },
  urlIcon: {
    color: tokens.colorBrandBackground
  },
  divider: {
    height: "20px"
  }
});

export default function PublishedSceneDialog({
  sceneName,
  screenshotUrl,
  status,
  versionType,
  productionUrl,
  draftUrl,
  productionStatus,
  draftStatus,
  ...props
}) {
  const styles = useStyles();
  const normalizedProductionUrl = toCurrentOriginUrl(productionUrl) || productionUrl;
  const normalizedDraftUrl = toCurrentOriginUrl(draftUrl) || draftUrl;

  return (
    <PreviewDialog imageSrc={screenshotUrl} title="Publish" cancelLabel={"Keep Editing"} {...props}>
      <div style={previewBody}>
        {status === "error" && !productionStatus && !draftStatus && (
          <div className={styles.versionTypeContainer}>
            <Badge size="large" appearance="outline" color="danger">
              <ErrorCircle20Regular fontSize={16} color="#BC4B09" />
              Error
            </Badge>
            <p className={styles.versionTypeDescription}>There was an error publishing your scene. Please try again.</p>
          </div>
        )}
        {(productionStatus || draftStatus) && (
          <div className={styles.versionTypeContainer}>
            {(versionType === VERSION_TYPES.PRODUCTION || versionType === VERSION_TYPES.BOTH) && productionStatus && (
              <>
                <Badge
                  size="large"
                  appearance="outline"
                  color={productionStatus.status === "success" ? "success" : "danger"}
                  className={styles.versionTypeBadge}
                >
                  {productionStatus.status === "success" ? <CheckmarkFilled /> : <ErrorCircle20Regular />}
                  Production
                </Badge>
                <p className={styles.versionTypeDescription}>
                  {productionStatus.status === "success"
                    ? "Your Verse has been published and is now available."
                    : "There was an error publishing to production. Try again."}
                </p>
                {normalizedProductionUrl && productionStatus.status === "success" && (
                  <a
                    href={normalizedProductionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.urlLink}
                  >
                    <span className={styles.urlText}>{normalizedProductionUrl}</span>
                    <Open16Filled className={styles.urlIcon} />
                  </a>
                )}
              </>
            )}
            {versionType === VERSION_TYPES.BOTH && productionStatus && draftStatus && (
              <Divider className={styles.divider} />
            )}
            {(versionType === VERSION_TYPES.DRAFT || versionType === VERSION_TYPES.BOTH) && draftStatus && (
              <>
                <Badge
                  size="large"
                  appearance="outline"
                  color={draftStatus.status === "success" ? "success" : "danger"}
                  className={styles.versionTypeBadge}
                >
                  {draftStatus.status === "success" ? <CheckmarkFilled /> : <ErrorCircle20Regular />}
                  Development
                </Badge>
                <p className={styles.versionTypeDescription}>
                  {draftStatus.status === "success"
                    ? "Your Verse has been published and is now available."
                    : "There was an error publishing to development. Try again."}
                </p>
                {normalizedDraftUrl && draftStatus.status === "success" && (
                  <a href={normalizedDraftUrl} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                    <span className={styles.urlText}>{normalizedDraftUrl}</span>
                    <Open16Filled className={styles.urlIcon} />
                  </a>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </PreviewDialog>
  );
}

PublishedSceneDialog.propTypes = {
  sceneName: PropTypes.string.isRequired,
  screenshotUrl: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  versionType: PropTypes.string.isRequired,
  productionUrl: PropTypes.string,
  draftUrl: PropTypes.string,
  productionStatus: PropTypes.shape({
    status: PropTypes.string,
    message: PropTypes.string
  }),
  draftStatus: PropTypes.shape({
    status: PropTypes.string,
    message: PropTypes.string
  })
};
